import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  isBefore,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { getWorkerAvailability, createAppointment, submitRescheduleRequest } from './api';
import { isNetworkError } from './detailMessage';
import ServiceUnavailablePage from './components/ServiceUnavailablePage';
import useAppointmentEvents from './useAppointmentEvents';
import BookingLoading from './booking/BookingLoading';
import CustomerForm from './booking/CustomerForm';
import MonthCalendar from './booking/MonthCalendar';
import MonthNav from './booking/MonthNav';
import SlotPicker, { SelectedSlot } from './booking/SlotPicker';
import parseBookingError from './booking/parseBookingError';
import Logo from './components/Logo';
import ConfirmShell from './components/ConfirmShell';
import useTheme from './useTheme';
import { useLang } from './LangContext';
import type { translations } from './i18n';
import { Location } from './types';
import './BookingPage.css';

interface TimeSlot {
  start: string;
  end: string;
}

interface AvailabilityEntry {
  date: string;
  hourly_slots?: TimeSlot[];
  free_slots?: TimeSlot[];
  available_ranges?: { start: string; end: string }[];
  message?: string;
}

interface WorkerAvailabilityResponse {
  worker_first_name: string;
  worker_last_name: string;
  worker_display_name: string | null;
  location: Location | null;
  logo_url: string | null;
  bio: string | null;
  booking_advance_days: number;
  duration_options: number[];
  default_duration: number;
  availability: AvailabilityEntry[];
}

interface FieldErrors {
  customerName?: string;
  customerEmail?: string;
  general?: string;
  [key: string]: string | undefined;
}

interface BookingLogoProps {
  url: string;
  onError: () => void;
}

function BookingLogo({ url, onError }: BookingLogoProps) {
  return (
    <div className="booking-logo-wrap">
      <img src={url} alt="" className="booking-logo-img" onError={onError} />
    </div>
  );
}

interface ValidateFormArgs {
  customerName: string;
  customerEmail: string;
  selectedSlot: SelectedSlot | null;
  t: typeof translations['en'];
}

const validateForm = ({ customerName, customerEmail, selectedSlot, t }: ValidateFormArgs): FieldErrors => {
  const errors: FieldErrors = {};
  if (!customerName.trim()) {
    errors.customerName = t.nameTooShort;
  } else if (customerName.trim().length < 2) {
    errors.customerName = t.nameTooShort;
  }
  if (!customerEmail.trim()) {
    errors.customerEmail = t.invalidEmail;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
    errors.customerEmail = t.invalidEmail;
  }
  if (!selectedSlot) {
    errors.general = t.pleaseSelectSlot;
  }
  return errors;
};

const countDaysWithSlots = (entries: AvailabilityEntry[]): number =>
  entries.reduce(
    (sum, e) => sum + ((e.hourly_slots || e.free_slots || []).length > 0 ? 1 : 0),
    0,
  );

export default function BookingPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const lockedDurationParam = searchParams.get('duration');
  const lockedDuration = lockedDurationParam ? parseFloat(lockedDurationParam) : null;
  const isDurationLocked =
    lockedDuration !== null &&
    !Number.isNaN(lockedDuration) &&
    lockedDuration >= 0.5 &&
    lockedDuration <= 8.0;
  const locationParam = searchParams.get('location');
  const requestedLocationId =
    locationParam && /^\d+$/.test(locationParam) ? parseInt(locationParam, 10) : null;
  const rescheduleToken = searchParams.get('reschedule') || null;
  const currentAppointmentStart = searchParams.get('current_start') || null;

  useTheme();
  const { lang, t } = useLang();

  // Worker meta
  const [workerFirstName, setWorkerFirstName] = useState<string | null>(null);
  const [workerLastName, setWorkerLastName] = useState<string | null>(null);
  const [workerDisplayName, setWorkerDisplayName] = useState<string | null>(null);
  const [appointmentLocation, setAppointmentLocation] = useState<Location | null>(null);
  const [workerLogoUrl, setWorkerLogoUrl] = useState<string | null>(null);
  const [workerBio, setWorkerBio] = useState<string | null>(null);
  const [logoImgError, setLogoImgError] = useState(false);

  // Calendar / availability
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [backendDown, setBackendDown] = useState(false);
  const [availability, setAvailability] = useState<AvailabilityEntry[]>([]);
  const [currentMonthDate, setCurrentMonthDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null);
  const [duration, setDuration] = useState(isDurationLocked ? lockedDuration! : 1.0);
  const [prevMonthSlots, setPrevMonthSlots] = useState<number | null>(null);
  const [nextMonthSlots, setNextMonthSlots] = useState<number | null>(null);
  const [bookingAdvanceDays, setBookingAdvanceDays] = useState(90);
  const [refreshTick, setRefreshTick] = useState(0);
  const defaultAppliedRef = useRef(false);

  // Form
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerDescription, setCustomerDescription] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rescheduleSubmitted, setRescheduleSubmitted] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const slotsSectionRef = useRef<HTMLDivElement>(null);

  useAppointmentEvents(publicId!, () => setRefreshTick((n) => n + 1));

  // Scroll el near the top of the viewport, but cap the scroll position so the
  // footer stays just off-screen. This prevents the page from scrolling all the
  // way to the bottom when the content is short.
  const scrollToWithoutFooter = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const desired = window.scrollY + rect.top - 16;
    const footer = document.querySelector('.app-footer') as HTMLElement | null;
    const footerHeight = footer ? footer.offsetHeight : 0;
    const max = document.documentElement.scrollHeight - window.innerHeight - footerHeight;
    window.scrollTo({ top: Math.min(desired, max), behavior: 'smooth' });
  };

  // After picking a date, slide the slot section into view (mostly mobile).
  useEffect(() => {
    if (!selectedDay || !slotsSectionRef.current) return;
    scrollToWithoutFooter(slotsSectionRef.current);
  }, [selectedDay]);

  // After picking a slot, scroll further so the form is in view.
  useEffect(() => {
    if (!selectedSlot || !formRef.current) return;
    const id = window.requestAnimationFrame(() => {
      if (formRef.current) scrollToWithoutFooter(formRef.current);
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedSlot]);

  // Availability fetch — current month plus sibling months for nav indicators,
  // all in parallel so the page only shows once every piece is ready.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const monthParam = format(currentMonthDate, 'yyyy-MM');
        const res: WorkerAvailabilityResponse = await getWorkerAvailability(
          publicId!,
          monthParam,
          duration,
          requestedLocationId,
          rescheduleToken,
        );
        if (cancelled) return;

        const advanceDays = typeof res.booking_advance_days === 'number'
          ? res.booking_advance_days
          : bookingAdvanceDays;
        const today = new Date();
        const horizon = addDays(today, advanceDays);
        const isCurrentMonth = (d: Date) =>
          d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth();

        const fetchSibling = async (date: Date): Promise<number | null> => {
          if (isBefore(endOfMonth(date), today) && !isCurrentMonth(date)) return 0;
          if (isBefore(horizon, startOfMonth(date))) return null;
          try {
            const r: WorkerAvailabilityResponse = await getWorkerAvailability(
              publicId!,
              format(date, 'yyyy-MM'),
              duration,
              null,
              rescheduleToken,
            );
            return countDaysWithSlots(r.availability || []);
          } catch {
            return null;
          }
        };

        const [prevSlots, nextSlots] = await Promise.all([
          fetchSibling(subMonths(currentMonthDate, 1)),
          fetchSibling(addMonths(currentMonthDate, 1)),
        ]);

        if (cancelled) return;

        setWorkerFirstName(res.worker_first_name);
        setWorkerLastName(res.worker_last_name);
        setWorkerDisplayName(res.worker_display_name);
        setAppointmentLocation(res.location || null);
        setWorkerLogoUrl(res.logo_url || null);
        setWorkerBio(res.bio || null);
        if (typeof res.booking_advance_days === 'number') {
          setBookingAdvanceDays(res.booking_advance_days);
        }
        if (
          Array.isArray(res.duration_options) &&
          res.duration_options.length > 0 &&
          !isDurationLocked &&
          !defaultAppliedRef.current
        ) {
          defaultAppliedRef.current = true;
          if (
            typeof res.default_duration === 'number' &&
            res.duration_options.includes(res.default_duration)
          ) {
            setDuration(res.default_duration);
          } else {
            setDuration(res.duration_options[0]);
          }
        }
        setAvailability(res.availability);
        setPrevMonthSlots(prevSlots);
        setNextMonthSlots(nextSlots);
        setSelectedDay(null);
        setSelectedSlot(null);
        setHasLoaded(true);
      } catch (err) {
        console.error('Error fetching availability', err);
        if (!cancelled) {
          if (isNetworkError(err)) setBackendDown(true);
          else setLoadFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentMonthDate, publicId, duration, isDurationLocked, refreshTick, requestedLocationId]);

  const handleFormChange = (field: string, value: string) => {
    if (field === 'customerName') setCustomerName(value);
    else if (field === 'customerEmail') setCustomerEmail(value);
    else if (field === 'customerDescription') setCustomerDescription(value);

    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (error) setError('');
  };

  const handleSlotSelect = (slot: SelectedSlot) => {
    setSelectedSlot(slot);
  };

  const bookSlot = async () => {
    setError('');
    setFieldErrors({});
    setIsSubmitting(true);

    if (rescheduleToken) {
      if (!selectedSlot) {
        setFieldErrors({ general: t.pleaseSelectSlot });
        setError(t.pleaseSelectSlot);
        setIsSubmitting(false);
        return;
      }
      try {
        const startTime = selectedSlot.start.split('T')[1]?.substring(0, 5) ?? selectedSlot.start;
        await submitRescheduleRequest(rescheduleToken, {
          day: format(selectedDay!, 'yyyy-MM-dd'),
          start_time: startTime,
        });
        setRescheduleSubmitted(true);
      } catch (err) {
        console.error('Error submitting reschedule request', err);
        const parsed = parseBookingError(err);
        setFieldErrors((prev) => ({ ...prev, ...parsed.fieldErrors }));
        if (parsed.error) setError(parsed.error);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const validationErrors = validateForm({ customerName, customerEmail, selectedSlot, t });
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      if (validationErrors.general) setError(validationErrors.general);
      setIsSubmitting(false);
      return;
    }

    const appointmentData = {
      customer_name: customerName,
      customer_e_mail: customerEmail,
      day: format(selectedDay!, 'yyyy-MM-dd'),
      start_time: selectedSlot!.start,
      duration,
      description: customerDescription || null,
      location_id: appointmentLocation ? appointmentLocation.id : null,
      lang,
    };

    try {
      await createAppointment(publicId!, appointmentData);
      navigate('/thank-you', {
        state: {
          customerName,
          customerEmail,
          day: appointmentData.day,
          slotStart: appointmentData.start_time,
          duration: appointmentData.duration,
          description: appointmentData.description,
        },
      });
    } catch (err) {
      console.error('Error booking appointment', err);
      const parsed = parseBookingError(err);
      setFieldErrors((prev) => ({ ...prev, ...parsed.fieldErrors }));
      if (parsed.error) setError(parsed.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const horizon = addDays(new Date(), bookingAdvanceDays);
  const nextMonthBeyondHorizon = isBefore(horizon, startOfMonth(addMonths(currentMonthDate, 1)));

  if (backendDown) return <ServiceUnavailablePage onRetry={() => window.location.reload()} />;
  if (!hasLoaded) return <BookingLoading failed={loadFailed} />;

  const fullName =
    workerDisplayName || `${workerFirstName ?? ''} ${workerLastName ?? ''}`.trim();
  const dayEntry = selectedDay
    ? availability.find((entry) => entry.date === format(selectedDay, 'yyyy-MM-dd'))
    : null;

  const activeLogoUrl = workerLogoUrl && !logoImgError ? workerLogoUrl : null;

  if (rescheduleSubmitted) {
    return (
      <ConfirmShell>
        <div className="confirm-status-icon ok" aria-hidden="true">✓</div>
        <h2>{t.rescheduleRequestSent}</h2>
        <p>{t.rescheduleRequestSentMessage}</p>
        <p className="close-tab-hint">{t.closeTab}</p>
      </ConfirmShell>
    );
  }

  return (
    <main className="booking-page">
      <div className="booking-container">
        {rescheduleToken && (
          <div className="reschedule-banner" role="status">
            {t.rescheduleRequestBanner}
          </div>
        )}
        <div className="booking-header">
          <Logo size={32} className="booking-header-logo" />
          <div className="booking-header-identity">
            <h2 className="booking-header-title">{rescheduleToken ? t.rescheduleAnAppointment : t.bookAnAppointment}</h2>
            <div className="booking-header-text">
              <p className="booking-subtitle">
                {t.with} <strong>{fullName}</strong>
              </p>
              {appointmentLocation && (
                <p className="booking-subtitle booking-location-line">
                  <span aria-hidden="true">📍 </span>
                  <strong>{appointmentLocation.name}</strong> — {appointmentLocation.address}
                </p>
              )}
            </div>
            {activeLogoUrl && (
              <BookingLogo url={activeLogoUrl} onError={() => setLogoImgError(true)} />
            )}
          </div>
        </div>

        {workerBio && (
          <div className="booking-bio-card">
            <h3 className="booking-bio-title">{t.bioSectionTitle}</h3>
            <p className="booking-bio">{workerBio}</p>
          </div>
        )}

        <div className="booking-content">
          <div className="calendar-section">
            <div className="calendar-controls">
              <MonthNav
                currentMonthDate={currentMonthDate}
                prevMonthSlots={prevMonthSlots}
                nextMonthSlots={nextMonthSlots}
                nextMonthDisabled={nextMonthBeyondHorizon}
                onPrev={() => setCurrentMonthDate((prev) => subMonths(prev, 1))}
                onNext={() => {
                  if (!nextMonthBeyondHorizon) {
                    setCurrentMonthDate((prev) => addMonths(prev, 1));
                  }
                }}
              />

              <div className="duration-selector">
                <label>{t.durationLabel}</label>
                <span className="duration-display">
                  {duration === 1 ? t.oneHour : t.nHours(duration)}
                </span>
              </div>
            </div>

            <MonthCalendar
              currentMonthDate={currentMonthDate}
              availability={availability}
              selectedDay={selectedDay}
              onSelect={setSelectedDay}
            />
          </div>

          <div className="booking-section" ref={slotsSectionRef}>
            <SlotPicker
              selectedDay={selectedDay}
              entry={dayEntry}
              duration={duration}
              selectedSlot={selectedSlot}
              onSelect={handleSlotSelect}
              currentStartIso={currentAppointmentStart}
            />
            {selectedSlot && rescheduleToken && (
              <div ref={formRef} style={{ marginTop: 'var(--spacing-lg)' }}>
                {error && <div className="error-message" role="alert">{error}</div>}
                <button
                  className="submit-button"
                  onClick={bookSlot}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t.rescheduleSubmitting : t.rescheduleSubmitButton}
                </button>
              </div>
            )}
            {selectedSlot && !rescheduleToken && (
              <CustomerForm
                ref={formRef}
                customerName={customerName}
                customerEmail={customerEmail}
                customerDescription={customerDescription}
                onChange={handleFormChange}
                onSubmit={bookSlot}
                error={error}
                fieldErrors={fieldErrors}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
